import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { CommonBaseState, initialCommonBaseState } from '../../interfaces/redux/common';
import { Track } from '../../interfaces/dtos/track';
import { deleteTracks, loadTrack, loadTracks, saveTrack, setActive, setInactive, updateUploadingPerc, uploadTracksFile } from './trackAction';
import { logoutReducers, resetErrors, setError } from '../common/commonActions';

export interface TrackState extends CommonBaseState {
    loadingItem: boolean;
    deleting: boolean;
    uploadingPerc?: number;
    busySettingActive: boolean;
    busySettingInactive: boolean;

    tracks: Track[];
}

const initialState: TrackState = {
    ...initialCommonBaseState,
    busySettingActive: false,
    busySettingInactive: false,

    tracks: []
};

export const trackSlice = createSlice({
    name: 'track',
    initialState,
    reducers: {
        errorOccured: (state, action: PayloadAction<string>) => {
            state.error = true;
            state.errorMessage = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Load tracks
            .addCase(loadTracks.pending, (state) => {
                state.loading = true;
            })
            .addCase(loadTracks.fulfilled, (state, action) => {
                state.loading = false;
                state.tracks = action.payload;
            })
            .addCase(loadTracks.rejected, (state, action) => {
                state.loading = false;
                state.error = true;
                state.errorMessage = `Could not load tracks:\r\n${action.error.message}`;
            })

            // Load track
            .addCase(loadTrack.pending, (state) => {
                state.loadingItem = true;
            })
            .addCase(loadTrack.fulfilled, (state, action) => {
                state.loadingItem = false;
                state.tracks = action.payload;
            })
            .addCase(loadTrack.rejected, (state, action) => {
                state.loadingItem = false;
                state.error = true;
                state.errorMessage = `Could not load track:\r\n${action.error.message}`;
            })

            // Save track
            .addCase(saveTrack.pending, (state) => {
                state.saving = true;
            })
            .addCase(saveTrack.fulfilled, (state, action) => {
                state.saving = false;
                state.tracks = action.payload;
            })
            .addCase(saveTrack.rejected, (state, action) => {
                state.saving = false;
                state.error = true;
                state.errorMessage = `Could not save track:\r\n${action.error.message}`;
            })

            // Delete tracks
            .addCase(deleteTracks.pending, (state) => {
                state.deleting = true;
            })
            .addCase(deleteTracks.fulfilled, (state, action) => {
                state.deleting = false;
                state.tracks = action.payload;
            })
            .addCase(deleteTracks.rejected, (state, action) => {
                state.deleting = false;
                state.error = true;
                state.errorMessage = `Could not delete tracks:\r\n${action.error.message}`;
            })

            // Set active
            .addCase(setActive.pending, (state) => {
                state.busySettingActive = true;
            })
            .addCase(setActive.fulfilled, (state, action) => {
                state.busySettingActive = false;
                state.tracks = action.payload;
            })
            .addCase(setActive.rejected, (state, action) => {
                state.busySettingActive = false;
                state.error = true;
                state.errorMessage = `Could not set tracks as active:\r\n${action.error.message}`;
            })

            // Set inactive
            .addCase(setInactive.pending, (state) => {
                state.busySettingInactive = true;
            })
            .addCase(setInactive.fulfilled, (state, action) => {
                state.busySettingInactive = false;
                state.tracks = action.payload;
            })
            .addCase(setInactive.rejected, (state, action) => {
                state.busySettingInactive = false;
                state.error = true;
                state.errorMessage = `Could not set tracks as inactive:\r\n${action.error.message}`;
            })

            // upload Tracks File
            .addCase(uploadTracksFile.pending, (state) => {
                state.uploadingPerc = 0;
            })
            .addCase(uploadTracksFile.fulfilled, (state) => {
                state.uploadingPerc = undefined;
            })
            .addCase(uploadTracksFile.rejected, (state, action) => {
                state.uploadingPerc = undefined;
                state.error = true;
                state.errorMessage = `Could not upload Excel file:\r\n${action.error.message}`;
            })

            // update Upload Perc
            .addCase(updateUploadingPerc.fulfilled, (state, action) => {
                state.uploadingPerc = action.payload;
            })

            // Set error
            .addCase(setError.fulfilled, (state, action) => {
                state.error = true;
                state.errorMessage = action.payload;
            })
            // Reset errors
            .addCase(resetErrors.fulfilled, (state) => {
                state.error = false;
            })
            // Logout
            .addCase(logoutReducers.fulfilled, () => {
                return { ...initialState };
            });
    }
});

export const { errorOccured } = trackSlice.actions;
export const trackReducerActions = trackSlice.actions;
export const trackState = (state: RootState) => state.track;
export default trackSlice.reducer;
